import {
  RiAlertLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiExternalLinkLine,
  RiRefreshLine,
} from "react-icons/ri";
import { ReportFile, SkipReason, UploadReport as Report } from "./reportModel";

export interface UploadReportProps {
  report: Report;
  busy?: boolean;
  onRetry(id: string): void;
  onRetryAll(ids: string[]): void;
  onStartOver(): void;
}

const SKIP_COPY: Record<SkipReason, string> = {
  "no-direction": "ยังไม่ได้เลือกมุม จึงไม่ถูกส่ง",
  "product-not-found": "ไม่พบสินค้านี้ในระบบ จึงไม่ถูกส่ง",
  "not-sent": "ยังไม่ได้ส่ง",
};

/** A photo that did not land, with the path it was aimed at and what to do about it. */
function ProblemRow({
  file,
  tone,
  detail,
  action,
}: {
  file: ReportFile;
  tone: "error" | "warn";
  detail: string;
  action?: JSX.Element;
}) {
  return (
    <li className="flex items-start gap-2 px-3 py-2">
      <span className={`mt-0.5 shrink-0 ${tone === "error" ? "text-red-600" : "text-amber-600"}`}>
        {tone === "error" ? (
          <RiErrorWarningLine size={15} aria-hidden="true" />
        ) : (
          <RiAlertLine size={15} aria-hidden="true" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        {/* Narrow screens give the path and the filename a line each, so the name — the
            part that says which photo this is — never loses to the path in a truncation.
            Secondary text is tinted from the panel's own hue rather than greyed, which
            washes out on a coloured ground. */}
        <p className="text-xs leading-5">
          <span className="block truncate text-amber-700/70 tabular-nums sm:inline">
            {file.barcode}/{file.matCode}/{file.section}/
          </span>
          <span className="block truncate font-semibold text-amber-950 sm:inline">
            {file.storedName}
          </span>
        </p>
        <p className={`text-xs leading-5 ${tone === "error" ? "text-red-700" : "text-amber-800"}`}>
          {detail}
        </p>
      </div>
      {action}
    </li>
  );
}

/**
 * The receipt for an upload run. It leads with what still needs doing, because a report
 * that opens with its successes buries the remaining work below the fold.
 */
export default function UploadReport({
  report,
  busy,
  onRetry,
  onRetryAll,
  onStartOver,
}: UploadReportProps) {
  const { total, succeeded, failed, skipped, products, complete } = report;
  const nothingLanded = succeeded === 0;

  // The groups below carry the colour — green for what is done, amber for what is not —
  // so the headline stays neutral. Two amber blocks stacked would make neither mean
  // anything; only the icon tints, and it tints with the outcome.
  const verdictIcon = complete
    ? "text-emerald-600"
    : nothingLanded
    ? "text-red-600"
    : "text-amber-600";

  const headline = complete
    ? `อัปโหลดสำเร็จทั้งหมด ${succeeded} รูป`
    : nothingLanded
    ? `อัปโหลดไม่สำเร็จ ${total} รูป`
    : `อัปโหลดสำเร็จ ${succeeded} จาก ${total} รูป`;

  const detail = [
    products.length > 0 ? `${products.length} matcode` : null,
    failed.length > 0 ? `${failed.length} รูปไม่สำเร็จ` : null,
    skipped.length > 0 ? `${skipped.length} รูปไม่ได้ส่ง` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section aria-label="สรุปผลการอัปโหลด" className="mt-4 space-y-4">
      <div className="flex flex-wrap items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <span className={`mt-0.5 shrink-0 ${verdictIcon}`}>
          {complete ? (
            <RiCheckLine size={18} aria-hidden="true" />
          ) : nothingLanded ? (
            <RiErrorWarningLine size={18} aria-hidden="true" />
          ) : (
            <RiAlertLine size={18} aria-hidden="true" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-6 text-gray-800">{headline}</p>
          {detail && <p className="mt-0.5 text-xs leading-5 text-gray-600">{detail}</p>}
        </div>
        {complete && (
          <button
            type="button"
            onClick={onStartOver}
            className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium leading-5 text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
          >
            อัปโหลดชุดใหม่
          </button>
        )}
      </div>

      {products.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-xs font-semibold leading-5 text-emerald-800">อัปโหลดแล้ว</h3>
          <ul className="divide-y divide-emerald-100 rounded-lg border border-emerald-200 bg-emerald-50">
            {products.map((p) => (
              <li key={p.productKey} className="px-3 py-2.5">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-sm font-semibold leading-6 text-emerald-950 tabular-nums">
                    {p.barcode}
                    <span className="mx-1 text-emerald-600">›</span>
                    {p.matCode}
                  </span>
                  {p.name && (
                    <span className="min-w-0 truncate text-xs leading-5 text-emerald-800">{p.name}</span>
                  )}
                  {/* Opens in a new tab so checking the record never costs you the receipt. */}
                  <a
                    href={`/product/${p.productId}/show`}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium leading-5 text-blue-700 transition-colors hover:bg-blue-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                  >
                    ดูสินค้า
                    <RiExternalLinkLine size={13} aria-hidden="true" />
                  </a>
                </div>
                <dl className="mt-1 space-y-0.5">
                  {p.sections.map((s) => (
                    <div key={s.section} className="flex gap-2 text-xs leading-5">
                      <dt className="w-16 shrink-0 font-medium text-emerald-700 sm:w-20">
                        {s.section}/
                      </dt>
                      <dd className="min-w-0 flex-1 text-emerald-900">
                        {s.files.map((f, i) => (
                          <span key={f.id} className="tabular-nums">
                            {i > 0 && <span className="mx-1 text-emerald-400">·</span>}
                            {f.storedName}
                          </span>
                        ))}
                      </dd>
                      <dd className="shrink-0 tabular-nums text-emerald-600">{s.files.length} รูป</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(failed.length > 0 || skipped.length > 0) && (
        <div>
          <div className="mb-1.5 flex items-center gap-2">
            <h3 className="text-xs font-semibold leading-5 text-amber-800">ต้องจัดการต่อ</h3>
            {failed.length > 0 && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onRetryAll(failed.map((f) => f.id))}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium leading-5 text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
              >
                <RiRefreshLine size={14} aria-hidden="true" />
                ลองใหม่ {failed.length} รูป
              </button>
            )}
          </div>
          <ul className="divide-y divide-amber-200/70 rounded-lg border border-amber-200 bg-amber-50">
            {failed.map((f) => (
              <ProblemRow
                key={f.id}
                file={f}
                tone="error"
                detail={f.error ? `อัปโหลดไม่สำเร็จ — ${f.error}` : "อัปโหลดไม่สำเร็จ"}
                action={
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onRetry(f.id)}
                    aria-label={`ลองใหม่ ${f.storedName}`}
                    className="shrink-0 rounded bg-white px-2 py-1 text-xs font-medium leading-5 text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                  >
                    ลองใหม่
                  </button>
                }
              />
            ))}
            {skipped.map((f) => (
              <ProblemRow
                key={f.id}
                file={f}
                tone="warn"
                detail={SKIP_COPY[f.reason ?? "not-sent"]}
              />
            ))}
          </ul>
        </div>
      )}

    </section>
  );
}
